import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useTheme, useDevice } from '@/hooks';
import naIcon from '@/assets/na-icon.svg';

export function SiteHeader() {
  const { username, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { devices, selectedDevice, selectDevice, clearSelectedDevice } = useDevice();
  const navigate = useNavigate();

  const [filterText, setFilterText] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    if (!dropdownOpen) return;
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
        setFilterText('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [dropdownOpen]);

  const selectedName = selectedDevice
    ? (selectedDevice.yachtName?.yachtName || selectedDevice.deviceProperties.deviceSerialStr || 'Unknown')
    : '';

  const filteredDevices = filterText
    ? devices.filter((d) =>
        (d.yachtName?.yachtName || d.deviceProperties.deviceSerialStr || '')
          .toLowerCase()
          .includes(filterText.toLowerCase()),
      )
    : devices;

  return (
    <header className="flex h-14 items-center justify-between border-b border-slate-200 bg-white px-4 dark:border-navy-700 dark:bg-navy-900">
      {/* Left — Brand */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => {
            clearSelectedDevice();
            navigate('/devices');
          }}
          className="flex items-center gap-3 hover:opacity-80"
        >
          <img src={naIcon} alt="Nautic Alert" className="h-8 w-8 rounded" />
          <span className="text-sm font-semibold text-slate-900 dark:text-white">
            Nautic Alert
          </span>
        </button>
      </div>

      {/* Center — Searchable Device Selector */}
      {devices.length > 0 && (
        <div className="relative flex items-center gap-2" ref={dropdownRef}>
          <img src={naIcon} alt="" className="h-4 w-4 shrink-0" />
          <div className="relative">
            <input
              type="text"
              value={filterText}
              onChange={(e) => { setFilterText(e.target.value); setDropdownOpen(true); }}
              onFocus={() => setDropdownOpen(true)}
              placeholder={selectedName || 'Select a vessel…'}
              className="w-48 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 placeholder:text-slate-500 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500 dark:border-navy-600 dark:bg-navy-800 dark:text-slate-200 dark:placeholder:text-slate-400"
            />
            {dropdownOpen && (
              <div className="absolute left-0 top-full z-50 mt-1 max-h-64 w-56 overflow-auto rounded-lg border border-slate-200 bg-white shadow-lg dark:border-navy-600 dark:bg-navy-800">
                <button
                  className="w-full px-3 py-2 text-left text-sm text-slate-500 hover:bg-slate-50 dark:text-slate-400 dark:hover:bg-navy-700"
                  onClick={() => {
                    clearSelectedDevice();
                    navigate('/devices');
                    setDropdownOpen(false);
                    setFilterText('');
                  }}
                >
                  — All Devices
                </button>
                {filteredDevices.map((d) => {
                  const name = d.yachtName?.yachtName || d.deviceProperties.deviceSerialStr || 'Unknown';
                  const isSelected = selectedDevice?.deviceProperties.deviceUUID === d.deviceProperties.deviceUUID;
                  return (
                    <button
                      key={d.deviceProperties.deviceUUID}
                      className={`w-full px-3 py-2 text-left text-sm transition-colors ${
                        isSelected
                          ? 'bg-teal-50 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400'
                          : 'text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-navy-700'
                      }`}
                      onClick={() => {
                        selectDevice(d.deviceProperties.deviceUUID);
                        navigate(`/device/${d.deviceProperties.deviceUUID}/tracking`);
                        setDropdownOpen(false);
                        setFilterText('');
                      }}
                    >
                      {name}
                    </button>
                  );
                })}
                {filteredDevices.length === 0 && (
                  <p className="px-3 py-2 text-sm text-slate-400 dark:text-slate-500">No results</p>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Right — Controls */}
      <div className="flex items-center gap-3">
        {/* Theme toggle */}
        <button
          onClick={toggleTheme}
          className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-navy-800"
          title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
        >
          {theme === 'dark' ? (
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          ) : (
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
            </svg>
          )}
        </button>

        {/* User info */}
        <span className="text-xs text-slate-500 dark:text-slate-400">
          {username}
        </span>

        {/* Logout */}
        <button
          onClick={logout}
          className="rounded-lg px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-navy-800"
        >
          Sign Out
        </button>
      </div>
    </header>
  );
}
